import { isPlatformBrowser } from '@angular/common';
import { ApplicationRef, inject, Injectable, PLATFORM_ID } from '@angular/core';
import { ActivatedRouteSnapshot, Resolve, RouterStateSnapshot } from '@angular/router';
import { EdependencyLevel } from '@df/catalog/model';
import { Route } from '@df/catalog/route/route';
import { RouteResolver } from '@df/catalog/route/route.resolver';
import { RouteResource } from '@df/catalog/route/route.resource';

/**
 * Pauses before each retry of a route lookup that failed without an answer from the API.
 */
const RETRY_DELAYS_MS = [250, 1000];

/**
 * True for the route lookup request (`/api/n/route/<path>`), the only request whose response is
 * carried from the server render to the browser in TransferState — see provideClientHydration in
 * app.config.ts.
 */
export const isRouteLookupRequest = (url: string): boolean => /\/api\/n\/route\//.test(url);

/**
 * Wraps dragonfly's RouteResolver so that a route lookup which fails in transit is never shown as
 * "page not found".
 *
 * Dragonfly's Model swallows load errors and RouteResolver resolves the empty model, so
 * catalog-view falls through to <error-404>: the URL stays the same but the body becomes the
 * "Ooops!" block, robots becomes NOINDEX and the canonical is removed. When that happens during
 * hydration it replaces a correctly server-rendered page — which is what Google's renderer was
 * recording for pages reported as "Duplicate, Google chose different canonical than user"
 * (ASR-438).
 *
 * A lookup the API answered with "not found" is a real 404 and is left alone. Anything else that
 * left the model empty (network error, timeout, 5xx) is retried before RouteResolver takes over.
 */
@Injectable({ providedIn: 'root' })
export class RouteLookupResolver implements Resolve<Route> {
    private readonly routeResolver = inject(RouteResolver);
    private readonly routeResource = inject(RouteResource);
    private readonly appRef = inject(ApplicationRef);
    private readonly platformId = inject(PLATFORM_ID);
    private refreshScheduled = false;

    async resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<Route> {
        const model = this.routeResource.getCreate(this.routeResolver.cleanUrl(state.url));

        await this.loadRoute(model, false);
        for (const delay of RETRY_DELAYS_MS) {
            if (!failedInTransit(model)) {
                break;
            }
            await new Promise(resolve => setTimeout(resolve, delay));
            await this.loadRoute(model, true);
        }

        if (isPlatformBrowser(this.platformId) && !this.refreshScheduled) {
            this.refreshScheduled = true;
            this.refreshOnceStable(model);
        }

        // The model is loaded (or has definitively failed) by now, so RouteResolver only adds the
        // dependencies and handles rewrites — it does not request the route again.
        return this.routeResolver.resolve(route, state);
    }

    /**
     * Loads the route itself only. Dependencies are left to RouteResolver so a retry does not
     * reload the target and everything under it.
     */
    private async loadRoute(model: Route, reload: boolean): Promise<void> {
        try {
            await model.load(1, { dependencyLevel: EdependencyLevel.none, reload });
        } catch {
            // Model records the failure on itself; failedInTransit() reads it from there.
        }
    }

    /**
     * On the first page view the route lookup is answered from the server render's TransferState,
     * which may come from the SSR cache and so carry old prices. Once the app is stable the transfer
     * cache is off, so this fetches the lookup live and lets the fresh catalog data replace it.
     * If that request fails the server-rendered data stays in place.
     */
    private refreshOnceStable(model: Route): void {
        this.appRef
            .whenStable()
            .then(() => model.load(1, { dependencyLevel: EdependencyLevel.none, reload: true }))
            .catch(() => {
                // Keep what the page already has.
            });
    }
}

/**
 * True when the route model has no data because the request failed, rather than because the API
 * answered that the path does not exist (`{"error":"404"}`).
 */
function failedInTransit(model: Route): boolean {
    if (model.data) {
        return false;
    }
    // loadModelStatus is protected on Model; it is the only place the load error is kept.
    const error = (model as unknown as { loadModelStatus?: { error?: unknown } }).loadModelStatus?.error;
    if (!error) {
        return false;
    }
    return String((error as { error?: unknown }).error) !== '404';
}
